import { createContext, useContext, useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";
import { logger } from "../lib/logger";

const SavedContext = createContext(null);

export function SavedProvider({ children }) {
  const { user } = useAuth();
  const [ids, setIds] = useState(new Set());

  const refresh = useCallback(async () => {
    if (!user) { setIds(new Set()); return; }
    try {
      const { data } = await api.get("/saved/ids");
      setIds(new Set(data));
    } catch (err) {
      logger.warn("saved: refresh failed", err);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const toggle = useCallback(async (listingId) => {
    if (!user) return { ok: false, reason: "unauth" };
    const isSaved = ids.has(listingId);
    try {
      if (isSaved) {
        await api.delete(`/saved/${listingId}`);
        setIds((prev) => { const n = new Set(prev); n.delete(listingId); return n; });
        return { ok: true, saved: false };
      }
      await api.post(`/saved/${listingId}`);
      setIds((prev) => new Set(prev).add(listingId));
      return { ok: true, saved: true };
    } catch (err) {
      logger.warn("saved: toggle failed", err);
      return { ok: false, reason: "error" };
    }
  }, [user, ids]);

  const value = useMemo(() => ({ ids, toggle, refresh }), [ids, toggle, refresh]);
  return <SavedContext.Provider value={value}>{children}</SavedContext.Provider>;
}

export const useSaved = () => useContext(SavedContext);
